import { type FC, useContext, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  useBuildQuarryMutation,
  useGetUserQuarriesForClaimQuery,
} from '@api/queries/quarry';
import { AppRoute } from '@common/app/routes';
import FirstBuild from '@common/components/FirstBuild/FirstBuild';
import { useCheckRequirements } from '@common/hooks/check-requirements';
import { IntroContext } from '@components/Intro/context';
import { updateStorageAndBalance } from '@redux/reducers/userReducer';
import { selectQuarryBuildRequirementsByResourceId } from '@redux/selectors/quarrySelectors';
import { useAppDispatch, useAppSelector } from '@redux/store';

import { completeNewLevelStep } from '@/common/lib/completeNewLevelStep';
import { firebaseLogUpgradeBuilding } from '@/common/lib/firebaseLog';
import { SendAnalitics } from '@/common/lib/playdeck';
import { selectNextStepPlant } from '@/redux/selectors/userSelectors';

import { QuarryBuildComplete } from '../QuarryBuildComplete/QuarryBuildComplete';

const QuarryBuild: FC = () => {
  const [skip, setSkip] = useState(false);
  useGetUserQuarriesForClaimQuery(undefined, {
    skip,
    refetchOnMountOrArgChange: true,
  });
  const [buildQuarry, { isLoading }] = useBuildQuarryMutation();
  const isFirstBuild = useAppSelector(
    (state) => state.quarry.quarries?.userQuarries?.length === 0,
  );
  const currentStep = useAppSelector(selectNextStepPlant);
  const [isConstructComplete, setIsConstructComplete] = useState(false);
  const nextToBuild = useAppSelector(
    (state) => state.quarry.quarries?.nextToBuild,
  );
  const requirementsList = useAppSelector(
    selectQuarryBuildRequirementsByResourceId,
  );
  const { isComplete } = useCheckRequirements(
    nextToBuild?.creationRequirements,
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { activeStep } = useContext(IntroContext);

  const onBuild = (): void => {
    if (!nextToBuild) return;
    const isIntro = activeStep === 2 || activeStep === 6;
    const { id: item_id, name: item, level: item_level } = nextToBuild;

    setSkip(true);
    buildQuarry({ id: item_id })
      .unwrap()
      .then((data) => {
        dispatch(updateStorageAndBalance(requirementsList));

        if (isIntro) {
          navigate(AppRoute.RESOURCES + `/mineResource/${data[0].id}`);
        } else {
          setIsConstructComplete(true);
        }
        SendAnalitics.buildDone({
          type: 'resources',
          item_id,
          item,
          item_level,
        });
        completeNewLevelStep({
          data: { name: nextToBuild?.name, level: 1 },
          dispatch,
          currentStep: {
            id: currentStep?.id,
            name: currentStep?.name,
            level: currentStep?.level,
          },
        });
      });

    if (nextToBuild && nextToBuild.creationRequirements)
      firebaseLogUpgradeBuilding(
        {
          name: nextToBuild.name,
          id: nextToBuild.id,
          level: nextToBuild.level,
        },
        nextToBuild.creationRequirements,
      );
  };

  if (!nextToBuild || !requirementsList) {
    return null;
  }

  return isConstructComplete ? (
    <QuarryBuildComplete name={nextToBuild.name} />
  ) : (
    <>
      <Helmet>
        <title>Quarry_Build</title>
      </Helmet>
      <FirstBuild
        isComplete={isComplete}
        requirementsList={requirementsList}
        kind="quarry"
        isFirstBuild={isFirstBuild}
        name={nextToBuild.name}
        onBuild={onBuild}
        isLoading={isLoading}
      />
    </>
  );
};

export { QuarryBuild };
